import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaUpload, FaTrash } from './Icons';
import { theme } from '../theme';
import { ImageStorageService } from '../services/ImageStorageService';

interface ImageUploaderProps {
  currentImageUrl: string | undefined;
  onImageChange: (imageUrl: string) => void;
  folder: string;
  placeholder?: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  flex: 1;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
  
  &::placeholder {
    color: ${theme.colors.text.hint};
  }
`;

const DeleteButton = styled.button`
  background: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #d32f2f;
  }
`;

const Separator = styled.div`
  display: flex;
  align-items: center;
  margin: 5px 0;
  
  &:before, &:after {
    content: '';
    flex: 1;
    height: 1px;
    background: #ddd;
  }
  
  span {
    padding: 0 10px;
    color: #666;
    font-size: 0.9rem;
  }
`;

const UploadButton = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 15px;
  background-color: ${theme.colors.primary.main};
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  width: 100%;
  box-sizing: border-box;
  
  &:hover {
    background-color: ${theme.colors.primary.dark};
  }
`;

const LoadingIndicator = styled.div`
  margin-top: 8px;
  color: ${theme.colors.primary.main};
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary.main};
  border-top-color: transparent;
  margin-right: 8px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PreviewContainer = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: center;
`;

const ImagePreview = styled.img`
  width: 180px;
  height: 120px;
  border-radius: 8px;
  object-fit: contain;
  border: 2px solid #eee;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
`;

const ImageUploader: React.FC<ImageUploaderProps> = ({
  currentImageUrl,
  onImageChange,
  folder,
  placeholder = "Pegar URL de imagen o hacer clic para subir"
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [urlValue, setUrlValue] = useState(currentImageUrl || '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Actualizar el estado local cuando cambia la prop
  React.useEffect(() => {
    setUrlValue(currentImageUrl || '');
  }, [currentImageUrl]);
  
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = e.target.value;
    setUrlValue(newUrl);
    onImageChange(newUrl);
  };
  
  const handleClearImage = () => {
    setUrlValue('');
    onImageChange('');
  };
  
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    setIsUploading(true);
    
    try {
      // Si ya hay una imagen, reemplazarla
      const imageUrl = await ImageStorageService.replaceImage(
        currentImageUrl,
        file,
        folder
      );
      
      setUrlValue(imageUrl);
      onImageChange(imageUrl);
    } catch (error) {
      console.error('Error al subir la imagen:', error);
      alert('No se pudo subir la imagen. Inténtalo de nuevo.');
    } finally {
      setIsUploading(false);
      // Limpiar el input para permitir subir la misma imagen de nuevo si es necesario
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  return (
    <Container>
      <div>
        <input
          type="file"
          id={`imageUpload_${folder}`}
          ref={fileInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleImageUpload}
        />
        
        <InputContainer>
          <Input
            type="text"
            value={urlValue}
            onChange={handleUrlChange}
            placeholder={placeholder}
            onClick={(e) => {
              // Prevent focusing the input when clicking the container
              if (e.target === e.currentTarget) {
                fileInputRef.current?.click();
              }
            }}
            style={{ cursor: 'pointer' }}
          />
          {urlValue ? (
            <DeleteButton onClick={handleClearImage} title="Eliminar imagen">
              <FaTrash size={16} />
            </DeleteButton>
          ) : (
            <UploadButton htmlFor={`imageUpload_${folder}`}>
              <FaUpload />
            </UploadButton>
          )}
        </InputContainer>
        {isUploading && (
          <LoadingIndicator>
            <Spinner />
            Subiendo imagen...
          </LoadingIndicator>
        )}
      </div>
      
      {/* Vista previa */}
      {urlValue && (
        <PreviewContainer>
          <ImagePreview
            src={urlValue}
            alt="Vista previa"
          />
        </PreviewContainer>
      )}
    </Container>
  );
};

export default ImageUploader;
